import React from "react";
import { PieChart, Pencil, Trash2, HandCoins } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const ProfitShare = ({ data }) => (
  <Card className="p-5 bg-card/40 border-gold/30" data-testid="profit-share-card">
    <p className="eyebrow text-gold mb-1 flex items-center gap-2"><PieChart className="w-4 h-4" /> Owner Profit Share</p>
    <p className="text-[11px] text-muted-foreground mb-4">Net profit is shared automatically after every payment by each owner's share %.</p>
    <div className="grid sm:grid-cols-3 gap-3">
      {data.distribution.map((d) => (
        <div key={d.name} data-testid={`share-${d.name.replace(/\W+/g, "-").toLowerCase()}`} className="p-3 rounded-lg bg-card/60 border border-border/50">
          <p className="text-sm font-medium">{d.name}</p>
          <p className="text-[11px] text-gold">{d.percentage}% of net profit</p>
          <p className="font-serif text-2xl text-emerald-300 mt-1">{inr(d.amount)}</p>
        </div>
      ))}
    </div>
    {data.net_profit <= 0 && <p className="text-[11px] text-amber-300 mt-3">No profit to share yet in this period.</p>}
  </Card>
);

export const PaymentsReceived = ({ data, onChanged }) => {
  const path = (p) => (p.kind === "member" ? `/franchise-payments/${p.id}` : `/payments/${p.payment_id}/ledger/${p.id}`);
  const edit = async (p) => {
    const v = window.prompt(`New amount for ${p.name} (₹)`, p.amount);
    if (v === null) return;
    try { await api.put(path(p), { amount: parseFloat(v) || 0 }); toast.success("Payment updated"); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete the payment of ${inr(p.amount)} from ${p.name}?`)) return;
    try { await api.delete(path(p)); toast.success("Payment deleted"); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Card className="p-5 bg-card/40 border-border/60" data-testid="payments-received-card">
      <p className="eyebrow text-gold mb-1 flex items-center gap-2"><HandCoins className="w-4 h-4" /> Payments Received</p>
      <p className="text-[11px] text-muted-foreground mb-3">Customers {inr(data.customer_revenue)} · Members {inr(data.member_revenue)}</p>
      <div className="space-y-2 max-h-[28rem] overflow-auto">
        {data.payments.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No payments received in this period.</p>}
        {data.payments.map((p) => (
          <div key={p.id} data-testid={`received-${p.id}`} className="p-2.5 rounded-lg bg-card/50 border border-border/50">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`text-[10px] ${p.kind === "member" ? "border-sky-400/40 text-sky-300" : "border-gold/40 text-gold"}`}>{p.kind === "member" ? "Member" : "Customer"}</Badge>
              <span className="text-sm flex-1 truncate">{p.name} <span className="text-[10px] text-muted-foreground">· {p.label}</span></span>
              <span className="text-xs text-muted-foreground">{p.date ? new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}</span>
              <span className="font-mono text-emerald-300 text-sm w-24 text-right">{inr(p.amount)}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-gold" data-testid={`received-edit-${p.id}`} onClick={() => edit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" data-testid={`received-delete-${p.id}`} onClick={() => del(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 font-mono">{p.split.map((s) => `${s.name} ${inr(s.amount)}`).join("  ·  ")}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};
